<script setup lang="ts">
import { VAvatar, VFloating, VIcon } from "@app/core";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import { usePartnershipStore } from "../store/partnershipStore";
import type { Partnership } from "../types";
import { PARTNERSHIP_LABELS } from "../types";

import { RouteNames, type RouteMeta } from "@/app/router/types";
import {
  findFirstAccessibleRoute,
  getEffectivePermissions,
  hasPermissions,
  routeMatchesPartnership,
} from "@/app/router/utils/guards";
import { useAuthStore } from "@/features/auth";
import { useGlobalFiltersStore } from "@/shared/stores/useGlobalFiltersStore";
import { useVendorFiltersStore } from "@/shared/stores/useVendorFiltersStore";

// Mirrors VFloating's Placement union (not exported from @app/core)
type FloatingPlacement = "bottom-left" | "bottom-right" | "top-left" | "top-right";

const { collapsed = false, placement = "bottom-left" } = defineProps<{
  collapsed?: boolean
  placement?: FloatingPlacement
}>();

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const partnershipStore = usePartnershipStore();
const filtersStore = useGlobalFiltersStore();
const vendorFiltersStore = useVendorFiltersStore();

const { user, userDisplayName } = storeToRefs(authStore);
const { partnership, partnerships, switching } = storeToRefs(partnershipStore);
const { switchPartnership } = partnershipStore;

// ── Icon colour cycle (preserved 1:1 from pim WorkspaceContextBlock) ──────────
const ICON_CLASSES = [
  "from-primary-600 to-primary",
  "from-success-600 to-success",
  "from-warning-600 to-warning",
] as const;

const activeLabel = computed(() => PARTNERSHIP_LABELS[partnership.value]);

const activeIndex = computed(() => {
  const idx = partnerships.value.indexOf(partnership.value);
  return idx >= 0 ? idx : 0;
});

const canSwitch = computed(() => partnerships.value.length > 1);

const partnershipIconClass = (index: number): string =>
  ICON_CLASSES[index % ICON_CLASSES.length];

const partnershipInitials = (p: Partnership): string =>
  PARTNERSHIP_LABELS[p]
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join("")
    .toUpperCase();

const onSwitch = async (target: Partnership, close: () => void) => {
  close();

  // Partnerships are separate analysis domains — start the new one with a clean
  // filter slate. Reset BEFORE the switch: shared filters (dates/channels/
  // granularity) live in the global store, so the new environment's requests
  // must not read the previous one's values after partnership flips. Awaiting
  // the URL reset also lands it before the redirect below, so router.beforeEach
  // does not re-inject the persisted filter params.
  vendorFiltersStore.resetSelection();
  await filtersStore.resetAllFilters();

  const switched = await switchPartnership(target);
  if (!switched) return;

  // The switch happens without navigation, so authGuard never re-runs — repeat
  // its checks here: the page must belong to the new partnership AND the user
  // must hold its effective permissions (vendorPermissions in Vendor Central).
  // Otherwise land on the first accessible route of the new partnership.
  const meta = route.meta as RouteMeta;
  const canStay = routeMatchesPartnership(meta.partnerships, target)
    && hasPermissions(getEffectivePermissions(meta, target));

  if (!canStay) {
    router.replace(findFirstAccessibleRoute(router.getRoutes()) ?? { path: "/403" });
  }
};

const goToPasswordChange = (close: () => void) => {
  close();
  router.push({ name: RouteNames.PASSWORD_CHANGE });
};

const handleLogout = (close: () => void) => {
  close();
  authStore.logout();
};
</script>

<template>
  <div class="lg:px-2 py-1.5">
    <VFloating
      class="!block"
      content-class="!p-0"
      :placement="placement"
    >
      <template #trigger>
        <button
          class="flex h-11 w-full items-center gap-2 rounded-lg border px-2
                 transition-colors duration-300 ease-in-out"
          :class="collapsed
            ? 'justify-center border-transparent hover:bg-base-300/50'
            : 'border-primary/20 bg-primary/10 hover:bg-primary/15 hover:border-primary/30'"
          type="button"
        >
          <!-- Partnership tile — always visible, fixed size to avoid layout jumps -->
          <div
            :class="partnershipIconClass(activeIndex)"
            class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br
                   font-extrabold text-[10px] text-white"
          >
            {{ partnershipInitials(partnership) }}
          </div>

          <!-- Partnership name + user line — fades out on collapse -->
          <Transition name="context-info-fade">
            <div
              v-if="!collapsed"
              class="flex min-w-0 flex-1 flex-col items-start leading-tight"
            >
              <span
                class="block max-w-[120px] overflow-hidden text-ellipsis
                       whitespace-nowrap text-xs font-semibold leading-tight text-mainText"
              >
                {{ activeLabel }}
              </span>
              <span class="text-[10.5px] leading-tight text-secondaryText">
                {{ userDisplayName }} · {{ user?.role }}
              </span>
            </div>
          </Transition>

          <!-- User avatar — fades out on collapse -->
          <Transition name="context-info-fade">
            <VAvatar
              v-if="!collapsed && user"
              :avatar="user.avatar"
              :custom-size="24"
              :name="user.name"
              shape="square"
            />
          </Transition>

          <!-- Chevron — fades out on collapse; swaps to a spinner while switching -->
          <Transition name="context-info-fade">
            <VIcon
              v-if="!collapsed"
              :loading="switching"
              :size="14"
              class="flex-shrink-0 text-primary/50"
              icon="lucide:chevron-down"
            />
          </Transition>
        </button>
      </template>

      <template #content="{ close }">
        <!-- Partnership header — display only, no navigation -->
        <div class="flex w-full items-center gap-2.5 border-b border-base-300 px-3 py-2.5">
          <div
            :class="[partnershipIconClass(activeIndex), 'h-8 w-8 rounded-lg text-[11px]']"
            class="flex flex-shrink-0 items-center justify-center bg-gradient-to-br
                   font-extrabold text-white"
          >
            {{ partnershipInitials(partnership) }}
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-semibold text-mainText">
              {{ activeLabel }}
            </div>
            <div class="text-[11px] text-secondaryText">
              Partnership
            </div>
          </div>
        </div>

        <!-- Partnership switcher — listed only when more than one is available -->
        <div
          v-if="canSwitch"
          class="border-b border-base-300 py-1"
        >
          <div
            class="px-3 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-wide
                   text-secondaryText/60"
          >
            Switch partnership
          </div>
          <button
            v-for="(p, i) in partnerships"
            :key="p"
            class="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors
                   duration-150 hover:bg-base-300/50 focus:outline-none focus-visible:outline-none
                   disabled:cursor-default disabled:hover:bg-transparent"
            type="button"
            :disabled="p === partnership || switching"
            @click="onSwitch(p, close)"
          >
            <div
              :class="[partnershipIconClass(i), 'h-7 w-7 rounded-md text-[10px]']"
              class="flex flex-shrink-0 items-center justify-center bg-gradient-to-br
                     font-extrabold text-white"
            >
              {{ partnershipInitials(p) }}
            </div>
            <span class="min-w-0 flex-1 truncate text-sm font-medium text-mainText">
              {{ PARTNERSHIP_LABELS[p] }}
            </span>
            <VIcon
              v-if="p === partnership"
              :size="16"
              class="flex-shrink-0 text-primary"
              icon="lucide:check"
            />
          </button>
        </div>

        <!-- User section — full row navigates to Password Change -->
        <button
          class="group flex w-full items-center gap-2.5 px-3 py-2.5
                 text-left transition-colors duration-150 hover:bg-base-300/50
                 focus:outline-none focus-visible:outline-none"
          type="button"
          @click="goToPasswordChange(close)"
        >
          <VAvatar
            :avatar="user?.avatar"
            :custom-size="28"
            :name="user?.name"
            shape="square"
          />
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-semibold text-mainText">
              {{ user?.name }}
            </div>
            <div class="truncate text-xs text-secondaryText">
              {{ user?.email }}
            </div>
          </div>
          <VIcon
            :size="14"
            class="ml-auto flex-shrink-0 text-secondaryText/40 transition-transform
                   duration-150 group-hover:translate-x-0.5 group-hover:text-secondaryText/70"
            icon="lucide:chevron-right"
          />
        </button>

        <div class="py-0.5">
          <button
            class="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left
                   text-sm font-medium text-error transition-colors duration-150
                   hover:bg-error/10 focus:outline-none focus-visible:outline-none"
            type="button"
            @click="handleLogout(close)"
          >
            <VIcon
              :size="18"
              icon="mdi:logout"
            />
            Logout
          </button>
        </div>
      </template>
    </VFloating>
  </div>
</template>

<style scoped>
/* Collapse: instant remove — no layout recalculation mid-animation */
.context-info-fade-leave-active { transition: none; }
.context-info-fade-leave-from   { opacity: 1; }
.context-info-fade-leave-to     { opacity: 0; }

/* Expand: fade in after sidebar finishes animating (300ms matches sidebar transition) */
.context-info-fade-enter-active { transition: opacity 0.15s ease 0.3s; }
.context-info-fade-enter-from   { opacity: 0; }
.context-info-fade-enter-to     { opacity: 1; }
</style>
